import { AuthError, confirmSignIn } from "aws-amplify/auth";
import { AlertCircle } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";

export function SelectMfa() {
  const navigate = useNavigate();
  const location = useLocation();
  const allowedMFATypes = location.state?.allowedMFATypes || [];
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSelectMfa = async (method: "authenticator" | "sms" | "email") => {
    setIsLoading(method);
    setError(null);
    try {
      if (method === "authenticator") {
        await confirmSignIn({
          challengeResponse: "TOTP",
        });
        navigate("/auth/verify-totp");
      } else if (method === "email") {
        const response = await confirmSignIn({
          challengeResponse: "EMAIL",
        });

        navigate("/auth/verify-email", {
          state: {
            // @ts-expect-error - codeDeliveryDetails is available in the response
            email: response.nextStep.codeDeliveryDetails?.destination,
          },
        });
      } else {
        await confirmSignIn({
          challengeResponse: "SMS",
        });
        navigate("/auth/verify-sms");
      }
    } catch (e) {
      const error = e as AuthError;

      setError(error.message);
    }
    setIsLoading(null);
  };

  const isEmailAllowed = allowedMFATypes.includes("EMAIL");
  const isSMSAllowed = allowedMFATypes.includes("SMS");
  const isAuthenticatorAllowed = allowedMFATypes.includes("TOTP");

  return (
    <div>
      <div className="px-4 sm:px-6">
        <p className="text-lg font-medium text-foreground">
          Choose your preferred method
        </p>
        <p className="text-base font-normal text-muted">
          You can use any of these methods to sign in
        </p>
        <div className="my-8 space-y-4 sm:my-11">
          {isAuthenticatorAllowed && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => handleSelectMfa("authenticator")}
              isLoading={isLoading === "authenticator"}
              disabled={isLoading === "sms"}
            >
              Use Authenticator App
            </Button>
          )}
          {isEmailAllowed && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => handleSelectMfa("email")}
              isLoading={isLoading === "email"}
              disabled={isLoading === "authenticator"}
            >
              Use Email
            </Button>
          )}
          {isSMSAllowed && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => handleSelectMfa("sms")}
              isLoading={isLoading === "sms"}
              disabled={isLoading === "authenticator"}
            >
              Send SMS code
            </Button>
          )}
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Authentication error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </div>
  );
}
